/**
 * NotebookPicker — Inline notebook selector for grounding chat responses.
 *
 * Shows a compact button that lets users select an active notebook.
 * When a notebook is selected, the Brain function receives the notebookId
 * and injects Gemini File Search for grounded, cited responses.
 */

import { useState } from "react";
import { Book, X, ChevronDown, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useNotebooks } from "@/hooks/queries/useNotebooks";

interface NotebookPickerProps {
  selectedNotebookId: string | undefined;
  onSelectNotebook: (notebookId: string | undefined) => void;
}

export function NotebookPicker({
  selectedNotebookId,
  onSelectNotebook,
}: NotebookPickerProps) {
  const { data: notebooks } = useNotebooks();
  const [open, setOpen] = useState(false);

  const activeNotebooks = (notebooks || []).filter(
    (nb) => nb.status === "active" && nb.gemini_store_id
  );

  const selected = activeNotebooks.find((nb) => nb.id === selectedNotebookId);

  if (activeNotebooks.length === 0) {
    return null; // No notebooks available — hide picker
  }

  return (
    <div className="flex items-center gap-1">
      {selected ? (
        <Badge
          variant="secondary"
          className="gap-1 pl-2 pr-1 py-0.5 text-xs cursor-pointer hover:bg-secondary/80 transition-colors"
        >
          <Book className="h-3 w-3 text-primary" />
          <span className="max-w-[120px] truncate">{selected.title}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-4 w-4 p-0 hover:bg-transparent"
            onClick={(e) => {
              e.stopPropagation();
              onSelectNotebook(undefined);
            }}
            aria-label="Clear notebook grounding"
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground"
              aria-label="Select notebook for grounding"
            >
              <Book className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Ground</span>
              <ChevronDown className="h-3 w-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <Command>
              <CommandInput placeholder="Search notebooks..." />
              <CommandList>
                <CommandEmpty>No notebooks found.</CommandEmpty>
                <CommandGroup heading="Notebooks">
                  {activeNotebooks.map((nb) => (
                    <CommandItem
                      key={nb.id}
                      value={nb.title}
                      onSelect={() => {
                        onSelectNotebook(nb.id);
                        setOpen(false);
                      }}
                    >
                      <Book className="h-4 w-4 mr-2 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <span className="truncate text-sm">{nb.title}</span>
                        <span className="text-xs text-muted-foreground ml-1">
                          ({nb.source_count} source{nb.source_count !== 1 ? "s" : ""})
                        </span>
                      </div>
                      {selectedNotebookId === nb.id && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
